import {
  Button,
  Checkbox,
  Divider,
  Menu,
  MenuDivider,
  MenuItem,
  Popover,
  Tag,
  Tooltip,
  Card,
  CompoundTag,
} from '@blueprintjs/core';
import styles from './RecordCardView.module.css';
import { ace, comment } from '@axo/shared/data-access/types';
import { CallIcon, CreatorTag } from '../CommunicationItem';
import { ICommentState, ICommentsState } from './useCommentsState';
import { CommentView } from './CommentView';
import { UseQueryResult } from 'react-query';
import { CreatedAtTag } from '../CommunicationItem/CreatedAtTag';
import cardClasses from '../CommunicationItem/Card.module.css';
import { RecordTags } from './RecordTags';
import { ace_hooks } from '@axo/shared/data-access/hooks';

interface RecordCardViewProps {
  record: ace.CallRecord;
  tags: ace.CallTag[];
  disabled: boolean;
  comments: ICommentState[];
  commentTemplates: UseQueryResult<comment.CommentTemplates>;
  commentsState: ICommentsState;
}

export const RecordCardView: React.FC<RecordCardViewProps> = ({
  record,
  disabled,
  tags,
  comments,
  commentTemplates,
  commentsState,
}) => {
  const { mutate: toggleIsCallAnswered, isLoading } =
    ace_hooks.useToggleIsCallRecordAnswered();

  return (
    <Card className={cardClasses.card}>
      <div className={cardClasses.topBar}>
        <CallIcon />
        <CreatorTag creatorID={record.ClientID} />
        <Tooltip
          content={
            <div
              style={{
                display: 'grid',
                gap: 'var(--margin-xsmall)',
              }}
            >
              <CompoundTag leftContent="Application status">
                <Tag>{record.LoanApplicationStatus}</Tag>
              </CompoundTag>

              {!!record.BucketName && (
                <CompoundTag leftContent="Bucket">
                  <Tag>{record.BucketName}</Tag>
                </CompoundTag>
              )}
            </div>
          }
        >
          <CreatedAtTag label="Called at:" createdAt={record.CreatedAt} />
        </Tooltip>

        <div className={cardClasses.fill} />
        <Checkbox
          className={styles.answeredCheckbox}
          label="Answer"
          checked={Boolean(record.Answered)}
          // If it has an OrderID, handling on Answered should be automatic
          disabled={disabled || isLoading || !!record.OrderID}
          onClick={() => {
            toggleIsCallAnswered({
              id: record.ID,
            });
          }}
        />
      </div>
      <div className={cardClasses.body}>
        <div className={styles.addComment}>
          <Popover
            interactionKind="hover"
            content={
              <Menu>
                <MenuDivider title="Select a template" />
                {commentTemplates.data?.Items.map((item) => (
                  <MenuItem
                    key={item.ID}
                    text={item.Name}
                    onClick={() => {
                      commentsState.addNewComment(item, record.ID);
                    }}
                  />
                ))}
              </Menu>
            }
          >
            <Button variant="minimal" icon="add" text="Add note" />
          </Popover>
        </div>
        {comments.map((comment) => (
          <div key={comment.original?.ID ?? 'new'} className={styles.comment}>
            <CommentView comment={comment} commentsState={commentsState} />
          </div>
        ))}
      </div>
      <Divider />
      <div className={cardClasses.bottomRow}>
        <RecordTags record={record} tags={tags} disabled={disabled} />
      </div>
    </Card>
  );
};
